import { createStyles, Modal, Stack } from '@mantine/core';
import type { LineageFlowMonitor, LineageFlowTest } from '@repo/api-codegen';
import { SegmentedControl } from '@repo/foundations';
import { useEffect, useMemo, useState } from 'react';

import MonitorItem from './MonitorItem';
import TestItem from './TestItem';

interface MonitorsAndTestsModalProps {
	opened: boolean;
	onClose: () => void;
	tests: LineageFlowTest[];
	monitors: LineageFlowMonitor[];
}

const useStyles = createStyles((theme) => ({
	content: {
		gap: 0,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		borderRadius: theme.other.borderRadius.md,
		'& > *:not(:last-child)': {
			borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		},
	},
}));

function MonitorsAndTestsModal({
	opened,
	onClose,
	tests,
	monitors,
}: MonitorsAndTestsModalProps) {
	const { classes } = useStyles();

	const hasMonitors = Boolean(monitors?.length);
	const hasTests = Boolean(tests?.length);

	const [controlValue, setControlValue] = useState<'monitors' | 'tests'>(
		'monitors'
	);

	useEffect(() => {
		if (hasMonitors && hasTests) setControlValue('monitors');
		else if (hasMonitors) setControlValue('monitors');
		else if (hasTests) setControlValue('tests');
	}, [hasMonitors, hasTests]);

	const title = useMemo(() => {
		if (hasMonitors && hasTests) return 'Monitors and tests';
		if (hasMonitors) return 'Monitors';
		if (hasTests) return 'Tests';
		return 'Monitors and tests';
	}, [hasMonitors, hasTests]);

	return (
		<Modal title={title} size="xl" opened={opened} onClose={onClose}>
			<Stack>
				{hasMonitors && hasTests && (
					<SegmentedControl
						value={controlValue}
						onChange={(value: 'monitors' | 'tests') => setControlValue(value)}
						data={[
							{ label: 'Monitors', value: 'monitors' },
							{ label: 'Tests', value: 'tests' },
						]}
						size="xs"
						fullWidth
					/>
				)}
				<Stack className={classes.content}>
					{controlValue === 'monitors'
						? monitors.map((monitor) => (
								<MonitorItem key={monitor.id} monitor={monitor} />
							))
						: tests.map((test) => <TestItem key={test.id} test={test} />)}
				</Stack>
			</Stack>
		</Modal>
	);
}

export default MonitorsAndTestsModal;
